import React, { useRef } from 'react';
import { Check, PencilLine } from 'lucide-react';
import { IconApiBook, IconPrinter } from '@tabler/icons-react';
import {
  ColorSwatchCellDv2,
  DefeitosColorPalettePopover,
  isDefeitoColorConfigured,
  parseTags,
  stripHtmlPreview,
  normSev,
  SEV_OPTS,
  TIPO_OS_FIELDS,
  normOsFlag,
} from './defeitosVerveTableUi';
import {
  Dv2BoardEditPopover,
  Dv2ClassifEditPopover,
  Dv2SevEditPopover,
} from './DefeitosVerveEditPopovers';
import { Dv2TipoOsInlinePicker } from './Dv2TipoOsInlinePicker';

function HabilitarCell({ row, api }) {
  const {
    rowEnabled,
    desabilitarOpenId,
    setDesabilitarOpenId,
    desabilitar,
    disablingId,
    enablingId,
    habilitar,
    feedback,
  } = api;
  const cod = String(row.TB01048_CODIGO).trim();
  const en = rowEnabled(row);

  return (
    <td className="ci2-td ci2-td-center ci2-td-permite">
      {en ? (
        <div className="ci2-permite-wrap">
          {desabilitarOpenId === cod ? (
            <button
              type="button"
              className="boolean-badge no condicoes-disable-badge"
              onClick={() => desabilitar(cod)}
              disabled={disablingId === cod}
              title="Desabilitar defeito no portal"
            >
              <span className="boolean-badge-dot" aria-hidden />
              <span className="boolean-badge-text">{disablingId === cod ? 'Aguarde…' : 'Desabilitar'}</span>
            </button>
          ) : null}
          <button
            type="button"
            className="ci2-lock-toggle on"
            onClick={() => setDesabilitarOpenId((p) => (p === cod ? null : cod))}
            disabled={!!disablingId}
            title="Defeito habilitado"
            aria-pressed="true"
          >
            <span className="ci2-knob">
              <Check size={7} strokeWidth={3} aria-hidden />
            </span>
          </button>
          {feedback && feedback.cod === cod && feedback.type === 'err' ? (
            <div className="condicoes-msg-erro">{feedback.msg}</div>
          ) : null}
        </div>
      ) : enablingId === cod ? (
        <span className="ci2-enabling">
          <span className="ci2-enabling-spinner" aria-hidden />
          Processando...
        </span>
      ) : (
        <button
          type="button"
          className="ci2-enable-affordance ci2-modern-tooltip-host"
          data-tooltip="Clique para habilitar defeito"
          onClick={() => habilitar(cod)}
          aria-label="Habilitar defeito"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      )}
    </td>
  );
}

function CellTooltipWrap({ tooltip, center, children }) {
  return (
    <span
      className={`ci2-modern-tooltip-host dv2-cell-tooltip-anchor${center ? ' dv2-cell-tooltip-anchor--center' : ''}`}
      data-tooltip={tooltip}
    >
      {children}
    </span>
  );
}

function EditableCellTrigger({ canEdit, tooltip, onStart, children, center = false, hideTooltip = false }) {
  if (!canEdit) return children;
  return (
    <button
      type="button"
      tabIndex={-1}
      className={`ci2-cell-trigger dv2-cell-trigger${center ? ' dv2-cell-trigger--center' : ''}`}
      onMouseDown={onStart}
    >
      {hideTooltip ? children : (
        <CellTooltipWrap tooltip={tooltip} center={center}>
          {children}
        </CellTooltipWrap>
      )}
    </button>
  );
}

export function DefeitosVerveTableRowV2({ row, cat, api, hidden }) {
  const {
    rowEnabled,
    isDefeitoColVis,
    editing,
    startEdit,
    cancelEdit,
    setModalRow,
    setEquipModalRow,
    tipoOsOpenCod,
    setTipoOsOpenCod,
    colorPaletteOpenCod,
    draftColor,
    openColorPalette,
    closeColorPalettePicker,
    saveColorPick,
  } = api;

  const boardAnchorRef = useRef(null);
  const classifAnchorRef = useRef(null);
  const sevAnchorRef = useRef(null);
  const tipoOsAnchorRef = useRef(null);
  const colorAnchorRef = useRef(null);

  const cod = String(row.TB01048_CODIGO).trim();
  const en = rowEnabled(row);
  const ed = editing?.cod === cod ? editing.field : null;
  const tipoOsOpen = tipoOsOpenCod === cod;

  const handleStart = (e, field) => {
    if (!en) return;
    e.preventDefault();
    e.stopPropagation();
    setTipoOsOpenCod(null);
    if (field === 'color') {
      colorAnchorRef.current = e.currentTarget;
      if (ed) cancelEdit();
      openColorPalette(row);
      return;
    }
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    if (field === 'board') boardAnchorRef.current = e.currentTarget;
    if (field === 'classif') classifAnchorRef.current = e.currentTarget;
    if (field === 'sev') sevAnchorRef.current = e.currentTarget;
    startEdit(row, field);
    requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
  };

  const handleTipoOsOpen = (e) => {
    if (!en) return;
    e.preventDefault();
    e.stopPropagation();
    if (ed) cancelEdit();
    closeColorPalettePicker();
    tipoOsAnchorRef.current = e.currentTarget;
    setTipoOsOpenCod((prev) => (prev === cod ? null : cod));
  };

  const sevLabel = SEV_OPTS.find((o) => o.v === normSev(row.severity_level))?.l || 'Leve';
  const nOn = TIPO_OS_FIELDS.reduce((acc, f) => acc + normOsFlag(row[f.db]), 0);
  const tipoLabel = nOn === TIPO_OS_FIELDS.length ? 'Todos' : `${nOn}/${TIPO_OS_FIELDS.length}`;

  return (
    <tr
      className={[
        'ci2-data-row',
        `cat-${cat}`,
        api.disablingId === cod ? 'condicoes-row-disabling' : '',
        hidden ? 'ci2-hidden-row' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <HabilitarCell row={row} api={api} />

      {isDefeitoColVis('codigo') ? (
        <td className="ci2-td dv2-td-code" title={cod}>
          {cod.length > 6 ? `${cod.slice(0, 6)}…` : cod}
        </td>
      ) : null}
      <td className="ci2-td dv2-td-nome" title={row.TB01048_NOME || ''}>
        <span className="dv2-defeito-name">{String(row.TB01048_NOME || '').slice(0, 56)}</span>
        {String(row.TB01048_NOME || '').length > 56 ? '…' : ''}
        {row.is_active === false ? <span className="dv2-inactive-tag">Inativo</span> : null}
        <button
          type="button"
          className="dv2-name-edit-btn"
          onClick={() => api.openEditService(cod)}
          aria-label="Editar serviço"
          title="Editar serviço"
        >
          <PencilLine size={13} strokeWidth={1.75} aria-hidden />
        </button>
      </td>

      {isDefeitoColVis('board') ? (
        <td className={`ci2-td dv2-td-center dv2-td-board${en ? ' ci2-td-editable' : ''}`}>
          <EditableCellTrigger
            canEdit={en}
            tooltip="Clique para configurar"
            center
            onStart={(e) => handleStart(e, 'board')}
          >
            <span className="ci2-mono">{en ? String(row.cod_alert || '').trim() || '—' : '—'}</span>
          </EditableCellTrigger>
        </td>
      ) : null}
      {isDefeitoColVis('color') ? (
        <td className={`ci2-td dv2-td-center dv2-td-color${en ? ' ci2-td-editable' : ''}`}>
          {en ? (
            <EditableCellTrigger
              canEdit={en}
              tooltip="Clique para configurar"
              center
              hideTooltip={isDefeitoColorConfigured(row.color_bg)}
              onStart={(e) => handleStart(e, 'color')}
            >
              <ColorSwatchCellDv2 hex={row.color_bg} />
            </EditableCellTrigger>
          ) : (
            <span className="defeitos-vv-muted">—</span>
          )}
        </td>
      ) : null}
      {isDefeitoColVis('classificacao') ? (
        <td className={`ci2-td${en ? ' ci2-td-editable' : ''}`}>
          <EditableCellTrigger canEdit={en} tooltip="Clique para configurar" onStart={(e) => handleStart(e, 'classif')}>
            <div className="defeitos-vv-tags-readonly dv2-tags-display">
              {en && row.classification
                ? parseTags(row.classification).map((t) => (
                    <span key={t} className="defeitos-vv-tag defeitos-vv-tag--readonly">
                      {t}
                    </span>
                  ))
                : en ? (
                    <span className="defeitos-vv-muted">—</span>
                  ) : (
                    <span className="defeitos-vv-muted">—</span>
                  )}
            </div>
          </EditableCellTrigger>
        </td>
      ) : null}
      {isDefeitoColVis('sugestao') ? (
        <td className={`ci2-td dv2-td-center${en ? ' ci2-td-editable' : ''}`}>
          {en ? (
            <button
              type="button"
              tabIndex={-1}
              className="ci2-cell-trigger dv2-cell-trigger dv2-cell-trigger--center dv2-sug-trigger"
              onClick={() => setModalRow(row)}
            >
              {stripHtmlPreview(row.auto_solution_suggestions) ? (
                <CellTooltipWrap tooltip={'✓ Configurado\nClique para editar sugestão'} center>
                  <span className="dv2-sug-icon-tip" aria-label="Sugestão configurada">
                    <IconApiBook size={16} stroke={1.75} className="dv2-sug-icon" aria-hidden />
                  </span>
                </CellTooltipWrap>
              ) : (
                <CellTooltipWrap tooltip="Clique para editar sugestão" center>
                  <span className="defeitos-vv-muted">—</span>
                </CellTooltipWrap>
              )}
            </button>
          ) : (
            <span className="defeitos-vv-muted">—</span>
          )}
        </td>
      ) : null}
      {isDefeitoColVis('criticidade') ? (
        <td className={`ci2-td dv2-td-center${en ? ' ci2-td-editable' : ''}`}>
          <EditableCellTrigger canEdit={en} tooltip="Clique para configurar" center onStart={(e) => handleStart(e, 'sev')}>
            <span className="ci2-mono">{en ? sevLabel : '—'}</span>
          </EditableCellTrigger>
        </td>
      ) : null}
      {isDefeitoColVis('tipo_os') ? (
        <td className={`ci2-td dv2-td-center${en ? ' ci2-td-editable' : ''}`}>
          {en ? (
            <button
              type="button"
              tabIndex={-1}
              ref={tipoOsAnchorRef}
              className={`ci2-cell-trigger dv2-cell-trigger dv2-cell-trigger--center${tipoOsOpen ? ' dv2-cell-trigger--open' : ''}`}
              onMouseDown={handleTipoOsOpen}
            >
              <CellTooltipWrap tooltip="Clique para configurar" center>
                <span className="ci2-mono">{tipoLabel}</span>
              </CellTooltipWrap>
            </button>
          ) : (
            <span className="defeitos-vv-muted">—</span>
          )}
        </td>
      ) : null}
      {isDefeitoColVis('equipamentos') ? (
        <td className={`ci2-td dv2-td-center${en ? ' ci2-td-editable' : ''}`}>
          {en ? (
            <button
              type="button"
              tabIndex={-1}
              className="ci2-cell-trigger dv2-cell-trigger dv2-cell-trigger--center dv2-equip-trigger"
              onClick={() => setEquipModalRow(row)}
            >
              {Number(row.equip_count) > 0 ? (
                <CellTooltipWrap
                  tooltip={`✓ Configurado\n${Number(row.equip_count)} modelo${Number(row.equip_count) === 1 ? '' : 's'} vinculado${Number(row.equip_count) === 1 ? '' : 's'}\nClique para configurar equipamentos`}
                  center
                >
                  <span className="dv2-equip-cell-inner">
                    <span className="ci2-mono">{Number(row.equip_count)}</span>
                    <span className="dv2-sug-icon-tip dv2-equip-icon-tip" aria-label="Equipamentos configurados">
                      <IconPrinter size={16} stroke={1.75} className="dv2-sug-icon" aria-hidden />
                    </span>
                  </span>
                </CellTooltipWrap>
              ) : (
                <CellTooltipWrap tooltip="Clique para configurar equipamentos" center>
                  <span className="defeitos-vv-muted">—</span>
                </CellTooltipWrap>
              )}
            </button>
          ) : (
            <span className="defeitos-vv-muted">—</span>
          )}
        </td>
      ) : null}

      {ed === 'board' ? <Dv2BoardEditPopover open anchorRef={boardAnchorRef} row={row} api={api} /> : null}
      {ed === 'classif' ? <Dv2ClassifEditPopover open anchorRef={classifAnchorRef} row={row} api={api} /> : null}
      {ed === 'sev' ? <Dv2SevEditPopover open anchorRef={sevAnchorRef} row={row} api={api} /> : null}
      {colorPaletteOpenCod === cod ? (
        <DefeitosColorPalettePopover
          open
          anchorRef={colorAnchorRef}
          draftColor={draftColor}
          onDraftChange={(hex) => saveColorPick(row, hex)}
          onClose={closeColorPalettePicker}
        />
      ) : null}
      {tipoOsOpen ? (
        <Dv2TipoOsInlinePicker
          open
          anchorRef={tipoOsAnchorRef}
          row={row}
          api={api}
          onClose={() => setTipoOsOpenCod(null)}
        />
      ) : null}
    </tr>
  );
}
